import { useTranslation } from '../i18n';
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Button, Slider, Space, Typography, message, Spin, Alert } from 'antd';
import { inlineCaptchaHost } from './InlineCaptcha';
import { SafetyOutlined, ReloadOutlined, CloseOutlined } from '@ant-design/icons';
import apiClient from '../services/apiClient';
import CaptchaLa from './CaptchaLa';
import { useTheme } from '../../theme/ThemeContext';

const { Text, Title } = Typography;

/**
 * Global Captcha Modal Component
 *
 * Listens for 'ratelimit:captcha' events from apiClient and shows
 * the appropriate captcha challenge.
 *
 * Usage: Add <CaptchaModal /> to your App.js root component
 */
// 浮层层级：需高于 antd 页面内容（Dropdown/Message ~1050 以下），
// 同时保持低于 captcha.la SDK 自己弹出的挑战层（其 challenge iframe/弹层
// 通常使用 2000000000 量级的超高 z-index）。取 ~1050 两边都安全；
// 若未来 SDK 挑战层被卡片遮挡，优先调低这里而不是调高。
const FLOAT_Z_INDEX = 1050;

const CaptchaModal = () => {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [captchaData, setCaptchaData] = useState(null);
    const [sliderValue, setSliderValue] = useState(0);
    const [mathAnswer, setMathAnswer] = useState('');
    const [eventDetail, setEventDetail] = useState(null);
    const { isDark } = useTheme();

    // Handle captcha event
    const handleCaptchaEvent = useCallback((event) => {
        console.log('[CaptchaModal] Received ratelimit:captcha event:', event.detail);
        // 表单内有承载位（InlineCaptcha）就在表单里验证，不弹窗
        const host = (event.detail?.captchaType || '') === 'captchala' ? inlineCaptchaHost() : null;
        if (host) {
            host.show(event.detail);
            return;
        }
        setEventDetail(event.detail);
        setVisible(true);
        const type = event.detail.captchaType || 'slider';
        // captcha.la 由第三方 SDK 渲染，无需向后端拉取自建挑战数据
        if (type !== 'captchala') {
            generateCaptcha(type);
        }
    }, []);

    // captcha.la 验证通过：携带 token 重试原请求（token 走 X-Captcha-Id，type=captchala）
    const handleCaptchalaSuccess = useCallback((token) => {
        setVisible(false);
        if (eventDetail?.onSuccess) {
            eventDetail.onSuccess(token, '', 'captchala');
        }
    }, [eventDetail]);

    // Listen for captcha events
    useEffect(() => {
        console.log('[CaptchaModal] Component mounted, listening for ratelimit:captcha events');
        window.addEventListener('ratelimit:captcha', handleCaptchaEvent);
        return () => {
            console.log('[CaptchaModal] Component unmounting, removing event listener');
            window.removeEventListener('ratelimit:captcha', handleCaptchaEvent);
        };
    }, [handleCaptchaEvent]);

    // Generate new captcha
    const generateCaptcha = async (type = 'slider') => {
        setLoading(true);
        setSliderValue(0);
        setMathAnswer('');

        try {
            const response = await apiClient.post('/captcha/generate', { type });
            if (response.data.success) {
                setCaptchaData(response.data.data);
            } else {
                message.error('Failed to load captcha');
            }
        } catch (error) {
            message.error('Failed to load captcha: ' + (error.response?.data?.error || error.message));
        } finally {
            setLoading(false);
        }
    };

    // Handle captcha submission - pass answer directly without separate verify call
    const verifyCaptcha = async () => {
        if (!captchaData) return;

        setVerifying(true);

        let answer;
        switch (captchaData.type) {
            case 'slider':
                answer = sliderValue;
                break;
            case 'simple':
                answer = parseInt(mathAnswer, 10);
                break;
            case 'click':
                // For click captcha, answer should be selected indices
                answer = [];
                break;
            default:
                answer = sliderValue;
        }

        // Close modal and pass captcha data to retry the original request
        // The RateLimitMiddleware will verify the captcha inline
        message.success('Verifying...');
        setVisible(false);

        // Call success callback with captcha_id, answer, and type
        // The original request will be retried with these headers
        if (eventDetail?.onSuccess) {
            eventDetail.onSuccess(
                captchaData.captcha_id,
                String(answer),  // X-Captcha-Answer
                captchaData.type // X-Captcha-Type
            );
        }

        setVerifying(false);
    };

    // Handle cancel
    const handleCancel = () => {
        setVisible(false);
        if (eventDetail?.onCancel) {
            eventDetail.onCancel();
        }
    };

    // Render slider captcha
    const renderSliderCaptcha = () => {
        const sliderData = captchaData?.slider;
        if (!sliderData) return null;

        return (
            <div style={{ padding: '20px 0' }}>
                <div
                    style={{
                        width: sliderData.width || 300,
                        height: sliderData.height || 150,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: 8,
                        position: 'relative',
                        marginBottom: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            left: `${sliderData.puzzle_x}%`,
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 40,
                            height: 40,
                            background: 'rgba(255,255,255,0.3)',
                            borderRadius: 4,
                            border: '2px dashed white'
                        }}
                    />
                    <div
                        style={{
                            position: 'absolute',
                            left: `${sliderValue}%`,
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 40,
                            height: 40,
                            background: 'rgba(255,255,255,0.9)',
                            borderRadius: 4,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <SafetyOutlined style={{ fontSize: 20, color: '#667eea' }} />
                    </div>
                </div>
                <Text type="secondary">Drag the slider to the target position</Text>
                <Slider
                    value={sliderValue}
                    onChange={setSliderValue}
                    min={0}
                    max={100}
                    tooltip={{ formatter: null }}
                    style={{ marginTop: 10 }}
                />
            </div>
        );
    };

    // Render simple math captcha
    const renderSimpleCaptcha = () => {
        const simpleData = captchaData?.simple;
        if (!simpleData) return null;

        return (
            <div style={{ padding: '20px 0', textAlign: 'center' }}>
                <Title level={3} style={{ marginBottom: 20 }}>
                    {simpleData.question}
                </Title>
                <input
                    type="number"
                    value={mathAnswer}
                    onChange={(e) => setMathAnswer(e.target.value)}
                    placeholder="Enter answer"
                    style={{
                        width: 150,
                        padding: '10px 15px',
                        fontSize: 18,
                        textAlign: 'center',
                        border: '2px solid #d9d9d9',
                        borderRadius: 8,
                        outline: 'none'
                    }}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            verifyCaptcha();
                        }
                    }}
                />
            </div>
        );
    };

    const isCaptchala = (eventDetail?.captchaType === 'captchala');

    // ESC 取消浮动卡片（对齐 Modal 的键盘可关闭行为）
    useEffect(() => {
        if (!(visible && isCaptchala)) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') handleCancel();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, isCaptchala, eventDetail]);

    // Render captcha content based on type (legacy slider/simple only)
    const renderCaptchaContent = () => {
        if (loading) {
            return (
                <div style={{ textAlign: 'center', padding: 40 }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>
                        <Text type="secondary">Loading captcha...</Text>
                    </div>
                </div>
            );
        }

        if (!captchaData) {
            return (
                <Alert
                    message="Failed to load captcha"
                    description="Please try again"
                    type="error"
                    showIcon
                />
            );
        }

        switch (captchaData.type) {
            case 'slider':
                return renderSliderCaptcha();
            case 'simple':
                return renderSimpleCaptcha();
            case 'click':
                // TODO: Implement click captcha
                return renderSliderCaptcha();
            default:
                return renderSliderCaptcha();
        }
    };

    // captcha.la：浮动卡片（非阻塞）。不用 antd Modal——无全屏遮罩，
    // 半透明背景层 pointer-events:none 点击穿透，页面保持可交互，
    // 只能通过右上角 X / ESC 取消，避免误触背景导致原请求被取消。
    const renderCaptchalaFloatCard = () => {
        if (!(visible && isCaptchala)) return null;

        const cardBg = isDark ? '#1f1f30' : '#ffffff';
        const cardBorder = isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #f0f0f0';
        const titleColor = isDark ? 'rgba(255, 255, 255, 0.88)' : 'rgba(0, 0, 0, 0.88)';
        const hintColor = isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)';
        const closeColor = isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)';

        return createPortal(
            <>
                <style>{`
                    @keyframes captchala-float-in {
                        from { opacity: 0; transform: translate(-50%, -8px); }
                        to { opacity: 1; transform: translate(-50%, 0); }
                    }
                `}</style>
                {/* 轻提示背景：仅微弱压暗聚焦视线，点击穿透，不阻塞页面 */}
                <div
                    aria-hidden="true"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: isDark ? 'rgba(0, 0, 0, 0.25)' : 'rgba(0, 0, 0, 0.08)',
                        pointerEvents: 'none',
                        zIndex: FLOAT_Z_INDEX - 1
                    }}
                />
                <div
                    role="dialog"
                    aria-label=
                            
                            {t('Security verification')}
                        
                        
                    style={{
                        position: 'fixed',
                        top: 88,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        // 见 FLOAT_Z_INDEX 注释：高于 antd 内容、低于 captcha.la 挑战 iframe
                        zIndex: FLOAT_Z_INDEX,
                        width: 360,
                        maxWidth: 'calc(100vw - 32px)',
                        background: cardBg,
                        border: cardBorder,
                        borderRadius: 12,
                        boxShadow: isDark
                            ? '0 6px 24px rgba(0, 0, 0, 0.55)'
                            : '0 6px 24px rgba(0, 0, 0, 0.15)',
                        padding: '16px 20px 18px',
                        animation: 'captchala-float-in 0.2s ease-out'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                        <SafetyOutlined style={{ color: '#1677ff', fontSize: 16, marginRight: 8 }} />
                        <span style={{ fontSize: 15, fontWeight: 600, color: titleColor, flex: 1 }}>{t('Security verification')}</span>
                        <button
                            type="button"
                            aria-label={t('Cancel verification')}
                            onClick={handleCancel}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                padding: 4,
                                lineHeight: 1,
                                color: closeColor,
                                fontSize: 14
                            }}
                        >
                            <CloseOutlined />
                        </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <CaptchaLa
                            product="embed"
                            action={eventDetail?.action || 'login'}
                            onVerify={handleCaptchalaSuccess}
                            onError={() => {}}
                        />
                        <span style={{ fontSize: 12, color: hintColor, marginTop: 10 }}>
                            {t('Complete the verification above to continue automatically.')}
                        </span>
                    </div>
                </div>
            </>,
            document.body
        );
    };

    // Get custom title and message from event detail
    const captchaTitle = eventDetail?.captchaTitle || 'Security Verification';
    const captchaMessage = eventDetail?.captchaMessage || 'Please complete verification to continue';

    // captcha.la 走浮动卡片；旧版自建 slider/simple 保持原 antd Modal 不变
    if (isCaptchala) {
        return renderCaptchalaFloatCard();
    }

    return (
        <Modal
            title={
                <Space>
                    <SafetyOutlined />
                    <span>{captchaTitle}</span>
                </Space>
            }
            open={visible}
            onCancel={handleCancel}
            footer={[
                <Button
                    key="refresh"
                    icon={<ReloadOutlined />}
                    onClick={() => generateCaptcha(captchaData?.type || 'slider')}
                    disabled={loading}
                >
                    Refresh
                </Button>,
                <Button key="cancel" onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button
                    key="verify"
                    type="primary"
                    onClick={verifyCaptcha}
                    loading={verifying}
                    disabled={loading || !captchaData}
                >
                    Verify
                </Button>
            ]}
            width={400}
            centered
            maskClosable={false}
        >
            <Alert
                message={captchaTitle}
                description={captchaMessage}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />
            {renderCaptchaContent()}
        </Modal>
    );
};

export default CaptchaModal;
